import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableRow, Paper } from '@mui/material';
import { ClinicHeader, InfoGrid, tableCellStyle } from '../../../../../utils/reportUtils';
import { OocyteRetrievalType } from '../../../../../components/Surgery/oocyteRetrieval/OocyteRetrival.helper';
import { usePatientContext } from '../../../../../components/patinetProvider';
import { toTitleCase } from '../../../../../utils/commonUtils';

interface Props {
  data: OocyteRetrievalType;
}

const OocyteRetrievalReport: React.FC<Props> = ({ data }) => {
  const { MainpatientDetails } = usePatientContext();

  const patientInfo: string[][] = [
    [
      'Patient Name',
      `${toTitleCase(MainpatientDetails?.prefix)} ${toTitleCase(MainpatientDetails?.first_name)} ${toTitleCase(MainpatientDetails?.last_name)}`.trim() || 'N/A',
      'Patient ID',
      MainpatientDetails?.medical_record_no ?? 'N/A',
    ],
    [
      'Age',
      MainpatientDetails?.age ?? 'N/A',
      'Gender',
      toTitleCase(MainpatientDetails?.gender) || 'N/A',
    ],
    [
      'Partner Name',
      `${toTitleCase(MainpatientDetails?.partner?.first_name)} ${toTitleCase(MainpatientDetails?.partner?.last_name)}`.trim() || 'N/A',
      'Contact',
      MainpatientDetails?.mobile_number ?? 'N/A',
    ],
  ];
  
  const infoGridData: string[][] = [
    ['Clinician', data.clinician_name ?? 'N/A', 'Embryologist', data.embryologist_name ?? 'N/A'],
    ['Witness Embryologist', data.witness_embryologist_name ?? 'N/A', 'Anesthetist', data.anesthetist_name ?? 'N/A'],
    ['Nurse', data.nurse_name ?? 'N/A', 'Anesthesia Type', data.type_of_anesthesia?.replace('_', ' ') ?? 'N/A'],
    ['Needle Type', data.type_of_needle ?? 'N/A', 'Sub-type', data.sub_type_of_needle ?? 'N/A'],
    ['Difficulty Level', data.difficulty_level ?? 'N/A', 'PAC Done', data.pre_anesthesia_checkup ? 'Yes' : 'No'],
  ];

  const timingDetails: string[][] = [
    ['Cycle Start Date', data.cycle_start_date ?? 'N/A', 'OPU Date', data.opu_date ?? 'N/A'],
    ['Start Time', data.start_time ?? 'N/A', 'End Time', data.end_time ?? 'N/A'],
    ['Created On', new Date(data.created_on ?? '').toLocaleString(), 'Tube ID', data.tube_id ?? 'N/A'],
    ['No. of Needles Used', data.no_of_needles_used?.toString() ?? '0', '', ''],
  ];

  const currentDateTime = new Date().toLocaleString();

  return (
    <Box sx={{ p: 5, backgroundColor: '#fff', color: '#333', fontFamily: 'Arial, sans-serif', position: 'relative', minHeight: '100%' }}>
      <ClinicHeader />

      <Box component="hr" sx={{ my: 1, borderColor: '#fff' }} />

      {/* Patient Details */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Patient Details</Typography>
        <InfoGrid data={patientInfo} />
      </Box>

      {/* Clinical Details */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Clinical & Procedure Info</Typography>
        <InfoGrid data={infoGridData} />
      </Box>

      {/* Timing Details */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Timing and Other Details</Typography>
        <InfoGrid data={timingDetails} />
      </Box>

      {/* Follicles and Oocytes */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Follicles and Oocytes</Typography>
        <Paper>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={tableCellStyle}>Left Follicles</TableCell>
                <TableCell sx={tableCellStyle}>{data.optimum_follicles_left ?? '-'}</TableCell>
                <TableCell sx={tableCellStyle}>Right Follicles</TableCell>
                <TableCell sx={tableCellStyle}>{data.optimum_follicles_right ?? '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={tableCellStyle}>Oocytes Retrieved (Left)</TableCell>
                <TableCell sx={tableCellStyle}>{data.oocytes_retrieved_left ?? '-'}</TableCell>
                <TableCell sx={tableCellStyle}>Oocytes Retrieved (Right)</TableCell>
                <TableCell sx={tableCellStyle}>{data.oocytes_retrieved_right ?? '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Procedure Findings */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Procedure Findings</Typography>
        <Typography variant="body2">{data.procedure_findings || '-'}</Typography>
      </Box>

      {/* Notes */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Notes</Typography>
        <Typography variant="body2">{data.notes || '-'}</Typography>
      </Box>

      {/* Footer Timestamp */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          color: '#666',
          fontStyle: 'italic',
        }}
      >
        Report generated on: {currentDateTime}
      </Typography>
    </Box>
  );
};

export default OocyteRetrievalReport;
